import { Link } from 'react-router-dom';
import { ROLE, say } from '../lib/labels';
import { label } from '../lib/format';
import type { Change, ViewSection } from '../lib/policy-view';

// Versions as post revisions, shared by the approval screen and the ICP version page: one version readable, the strip
// of every version, and any two side by side with what changed marked.

export type VersionRef = { id: string; version: number; status: string; created_at?: string };
export type Signature = { role: string; decision: string; created_at: string; comment?: string | null };

export const day = (t?: string | null) => (t ? new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

/** One version, readable: its sections and items; in a comparison each item carries how it changed. */
export function ReadView({ view, marks, side, label: aria }: { view: ViewSection[]; marks?: Map<string, Change>; side?: 'old' | 'new'; label: string }) {
  return (
    <div className="pv" role="group" aria-label={aria}>
      {view.map((sec) => (
        <section key={sec.key} className="pv-sec">
          <h3>{sec.title}</h3>
          {sec.items.length ? sec.items.map((it) => {
            const m = marks?.get(`${sec.key}/${it.key}`);
            const tag = m === 'added' ? 'New' : m === 'removed' ? 'Removed' : m === 'changed' ? (side === 'old' ? 'Before' : 'Changed') : null;
            return (
              <article key={it.key} className={`pv-item${m && m !== 'same' ? ` pv-${m}` : ''}`}>
                <h4>{it.title}{tag ? <span className="pv-tag">{tag}</span> : null}</h4>
                <dl>{it.rows.map((r) => (
                  <div key={r.label} className="pv-row"><dt>{r.label}</dt><dd>{Array.isArray(r.value) ? <span className="pv-chips">{r.value.map((v) => <span key={v} className="chip">{v}</span>)}</span> : r.value}</dd></div>
                ))}</dl>
              </article>
            );
          }) : <p className="muted small">None.</p>}
        </section>
      ))}
    </div>
  );
}

/**
 * Every version in a row: the one on screen, the one it is compared with, a button to compare with any other and a
 * link to open it. `canCompare` false hides the Read | Compare switch (nothing to compare with).
 */
export function VersionStrip({ versions, currentId, baseVersion, mode, onMode, onPick, href, statusLabel, canCompare = true }: {
  versions: VersionRef[]; currentId: string; baseVersion?: number; mode: 'read' | 'compare'; onMode: (m: 'read' | 'compare') => void;
  onPick: (version: number) => void; href: (v: VersionRef) => string; statusLabel: (v: VersionRef) => string; canCompare?: boolean;
}) {
  return (
    <nav className="pv-versions" aria-label="Versions">
      <span className="muted small">Versions</span>
      <ol>{versions.map((v) => (
        <li key={v.id} className={v.id === currentId ? 'current' : v.version === baseVersion && mode === 'compare' ? 'base' : ''}>
          {v.id === currentId ? <span aria-current="page">v{v.version} · {statusLabel(v)}</span>
            : <button type="button" className="link" title={`Compare with version ${v.version}`} onClick={() => { onPick(v.version); onMode('compare'); }}>v{v.version} · {statusLabel(v)}</button>}
          <small className="muted">{day(v.created_at)}</small>
          {v.id !== currentId ? <Link className="small" to={href(v)}>open</Link> : null}
        </li>
      ))}</ol>
      {canCompare ? (
        <div className="seg-text" role="radiogroup" aria-label="View">
          <button type="button" role="radio" aria-checked={mode === 'read'} onClick={() => onMode('read')}>Read</button>
          <button type="button" role="radio" aria-checked={mode === 'compare'} onClick={() => onMode('compare')}>Compare{baseVersion !== undefined ? ` with v${baseVersion}` : ''}</button>
        </div>
      ) : null}
    </nav>
  );
}

/** Two versions side by side, the older on the left; `marks` from itemChanges(older, newer). */
export function CompareView({ older, newer, marks, olderTitle, newerTitle, olderLabel, newerLabel }: {
  older: ViewSection[]; newer: ViewSection[]; marks: Map<string, Change>; olderTitle: string; newerTitle: string; olderLabel?: string; newerLabel?: string;
}) {
  return (
    <div className="pv-compare" role="group" aria-label="Changes">
      <div><p className="pv-col">{olderTitle}</p><ReadView view={older} marks={marks} side="old" label={olderLabel ?? olderTitle} /></div>
      <div><p className="pv-col">{newerTitle}</p><ReadView view={newer} marks={marks} side="new" label={newerLabel ?? newerTitle} /></div>
    </div>
  );
}

export const changeCount = (marks?: Map<string, Change>) => (marks ? [...marks.values()].filter((m) => m !== 'same').length : 0);

/** Who signed, rejected or withdrew, with their words. */
export function Signatures({ approvals }: { approvals?: Signature[] }) {
  return approvals?.length
    ? <ul className="pv-signs">{approvals.map((a, i) => <li key={i}><b>{say(ROLE, a.role)}</b> {a.decision === 'approve' ? 'approved' : a.decision === 'reject' ? 'rejected' : 'withdrew'} on {day(a.created_at)}{a.comment ? `: “${a.comment}”` : ''}</li>)}</ul>
    : <p className="muted small">No signatures yet.</p>;
}
